<template>
  <page-header-wrapper>
    <a-card :bordered="false">
      <div class="catalog-heading">
        <div>
          <h2>{{ pageTitle }}</h2>
          <p>{{ pageDescription }}</p>
        </div>
        <a-button v-if="writable" type="primary" v-hasPermi="['dkmall:category:add']" @click="openCreate">
          <a-icon type="plus" />新增分类
        </a-button>
      </div>

      <a-table
        row-key="aid"
        :loading="loading"
        :columns="columns"
        :data-source="treeData"
        :pagination="false"
        bordered
      >
        <template slot="thumb" slot-scope="text">
          <a-avatar v-if="text" shape="square" :size="48" :src="text" />
          <span v-else class="muted">未设置</span>
        </template>
        <template slot="backgroundImage" slot-scope="text, record">
          <template v-if="Number(record.level) > 1"><span class="muted">继承上级</span></template>
          <template v-else>
            <a-avatar v-if="text" shape="square" :size="48" :src="text" />
            <span v-else class="muted">未设置</span>
          </template>
        </template>
        <template slot="colour" slot-scope="text, record">
          <span v-if="Number(record.level) > 1" class="muted">继承</span>
          <span v-else-if="text" class="colour-value"><i class="colour-swatch" :style="{ backgroundColor: text }" />{{ text }}</span>
          <span v-else class="muted">未设置</span>
        </template>
        <template slot="themes" slot-scope="text, record">
          <span v-if="record.themeNames">{{ record.themeNames }}</span>
          <span v-else-if="record.themeIds">{{ themeLabels(record.themeIds) }}</span>
          <span v-else class="muted">未关联</span>
        </template>
        <template slot="service" slot-scope="text, record">
          <div v-if="record.userId" class="service-cell">
            <a-avatar :size="28" :src="record.serviceAvatar" icon="user" />
            <div>
              <div>{{ record.serviceName || ('ID ' + record.userId) }}</div>
              <div class="muted">{{ record.serviceMobile || '-' }} · #{{ record.userId }}</div>
            </div>
          </div>
          <span v-else class="muted">未绑定</span>
        </template>
        <template slot="status" slot-scope="text, record">
          <a-tag
            :color="Number(text) === 1 ? 'green' : 'red'"
            class="status-tag"
            @click="writable && toggleStatus(record)"
          >{{ Number(text) === 1 ? '启用' : '停用' }}</a-tag>
        </template>
        <template slot="operation" slot-scope="text, record">
          <template v-if="writable">
            <a v-hasPermi="['dkmall:category:edit']" @click="openEdit(record)"><a-icon type="edit" /> 修改</a>
            <a-divider type="vertical" />
            <a-popconfirm title="删除后该分类将不再显示，确认删除？" @confirm="remove(record)">
              <a v-hasPermi="['dkmall:category:remove']"><a-icon type="delete" /> 删除</a>
            </a-popconfirm>
          </template>
          <span v-else class="muted">只读汇总</span>
        </template>
      </a-table>

      <a-modal :title="form.aid ? '编辑分类' : '新增分类'" :visible="modalVisible" :confirm-loading="saving" width="640px" @ok="submit" @cancel="closeModal">
        <a-form-model ref="categoryForm" :model="form" :rules="rules" :label-col="{ span: 6 }" :wrapper-col="{ span: 17 }">
          <a-form-model-item label="上级分类" prop="pid">
            <a-tree-select v-model="form.pid" :tree-data="parentOptions" :replace-fields="{ title: 'name', value: 'aid', key: 'aid' }" tree-default-expand-all allow-clear placeholder="不选择表示一级分类" @change="onPidChange" />
          </a-form-model-item>
          <a-form-model-item label="分类名称" prop="name"><a-input v-model="form.name" maxlength="100" /></a-form-model-item>
          <a-form-model-item label="英文名称" prop="nameEn"><a-input v-model="form.nameEn" maxlength="100" /></a-form-model-item>
          <a-form-model-item label="分类图标">
            <file-upload :value="form.thumb" type="image" :count="1" @change="value => form.thumb = value" />
            <div class="upload-hint">建议尺寸 200×200px，单张不超过 2MB</div>
          </a-form-model-item>
          <template v-if="formLevel <= 1">
            <a-form-model-item label="背景图">
              <file-upload :value="form.backgroundImage" type="image" :count="1" @change="value => form.backgroundImage = value" />
              <div class="upload-hint">建议尺寸 375×208px（金刚区分类顶部背景图）；二级/三级自动继承一级</div>
            </a-form-model-item>
            <a-form-model-item label="页面主题色">
              <div class="colour-field">
                <input v-model="form.colour" class="colour-picker" type="color" aria-label="选择页面主题色" />
                <a-input v-model="form.colour" placeholder="例如 #C9823F" />
              </div>
              <div class="upload-hint">控制搜索按钮、分类选中框等主题色；子级继承</div>
            </a-form-model-item>
          </template>
          <a-alert v-else message="二级/三级分类的颜色与背景图自动继承上级，无需单独配置" type="info" show-icon style="margin-bottom:16px" />
          <a-form-model-item v-if="scope === 'DK' && formLevel === 1" label="客服">
            <a-select
              v-model="form.userId"
              show-search
              allow-clear
              option-filter-prop="label"
              placeholder="选择客服"
            >
              <a-select-option v-for="a in agents" :key="a.id" :value="a.id" :label="agentLabel(a)">
                <div class="agent-option">
                  <a-avatar :size="22" :src="a.avatar" icon="user" />
                  <span>{{ agentLabel(a) }}</span>
                </div>
              </a-select-option>
            </a-select>
          </a-form-model-item>
          <a-form-model-item label="排序"><a-input-number v-model="form.sort" style="width:100%" /><div class="upload-hint">数值越小越靠前，同值按 ID 升序；所有层级可独立设置</div></a-form-model-item>
          <a-form-model-item label="状态"><a-switch v-model="statusEnabled" checked-children="启用" un-checked-children="停用" /></a-form-model-item>
        </a-form-model>
      </a-modal>
    </a-card>
  </page-header-wrapper>
</template>

<script>
import { listCategories, addCategory, updateCategory, deleteCategory, toggleCategoryStatus } from '@/api/dkmall/catalog'
import { listServiceAgents } from '@/api/dkmall/customer-service'
import { listThemes } from '@/api/dkmall/promotion'

const emptyForm = () => ({ aid: undefined, pid: 0, name: '', nameEn: '', thumb: '', backgroundImage: '', colour: '', themeIds: '', userId: undefined, sort: 100, status: 1, isShow: 1, level: 1 })

export default {
  name: 'DkMallCategory',
  data() {
    return {
      loading: false,
      saving: false,
      modalVisible: false,
      list: [],
      agents: [],
      themes: [],
      themeIdList: [],
      form: emptyForm(),
      rules: {
        name: [{ required: true, message: '请输入分类名称', trigger: 'blur' }],
        nameEn: [{ required: true, message: '请输入英文名称', trigger: 'blur' }]
      },
      baseColumns: [
        { title: '分类名称', dataIndex: 'name', width: 200 },
        { title: '英文名称', dataIndex: 'nameEn', width: 180 },
        { title: '图标', dataIndex: 'thumb', scopedSlots: { customRender: 'thumb' }, width: 90, align: 'center' },
        { title: '顶部背景', dataIndex: 'backgroundImage', scopedSlots: { customRender: 'backgroundImage' }, width: 100, align: 'center' },
        { title: '颜色', dataIndex: 'colour', scopedSlots: { customRender: 'colour' }, width: 90, align: 'center' },
        { title: '客服', scopedSlots: { customRender: 'service' }, width: 220, key: 'service' },
        { title: '排序', dataIndex: 'sort', width: 90, align: 'center' },
        { title: '状态', dataIndex: 'status', scopedSlots: { customRender: 'status' }, width: 90, align: 'center' },
        { title: '操作', scopedSlots: { customRender: 'operation' }, width: 170, align: 'center' }
      ]
    }
  },
  computed: {
    scope() {
      const path = String(this.$route.path || '').toLowerCase()
      if (path.includes('dk-category')) return 'DK'
      if (path.includes('custom-category')) return 'CUSTOM'
      return 'ALL'
    },
    writable() { return this.scope !== 'ALL' },
    columns() { return this.baseColumns.filter(column => this.scope !== 'CUSTOM' || column.key !== 'service') },
    pageTitle() { return this.scope === 'DK' ? 'DK 分类' : this.scope === 'CUSTOM' ? '定制分类' : '商品分类' },
    pageDescription() { return this.scope === 'DK' ? '管理 DK 分类、视觉、排序和一级分类现货客服归属；排序数值越小越靠前' : this.scope === 'CUSTOM' ? '管理定制分类、视觉和排序；排序数值越小越靠前' : '汇总查看全部分类数据' },
    treeData() { return this.buildTree(this.list) },
    parentOptions() { return [{ aid: 0, name: '一级分类（无上级）' }, ...this.buildTree(this.list.filter(item => String(item.aid) !== String(this.form.aid)))] },
    statusEnabled: {
      get() { return Number(this.form.status) === 1 },
      set(value) { this.form.status = value ? 1 : 0 }
    },
    formLevel() {
      if (!Number(this.form.pid)) return 1
      const parent = this.list.find(i => String(i.aid) === String(this.form.pid))
      const pl = parent && parent.level ? Number(parent.level) : 1
      return Math.min(pl + 1, 3)
    }
  },
  watch: {
    '$route.fullPath' () { this.load(); if (this.scope === 'DK') this.loadAgents() }
  },
  created() { this.load(); if (this.scope === 'DK') this.loadAgents() },
  methods: {
    buildTree(rows) {
      const nodes = rows.map(item => ({ ...item, children: [] }))
      const byId = Object.fromEntries(nodes.map(item => [String(item.aid), item]))
      const roots = []
      nodes.forEach(item => {
        const parent = byId[String(item.pid)]
        if (parent && String(item.pid) !== String(item.aid)) parent.children.push(item)
        else roots.push(item)
      })
      nodes.forEach(item => { if (!item.children.length) delete item.children })
      return roots
    },
    load() {
      this.loading = true
      listCategories(this.scope).then(res => { this.list = res.data || [] }).finally(() => { this.loading = false })
    },
    loadAgents() {
      listServiceAgents({ pageNum: 1, pageSize: 100, status: 1 }).then(res => {
        this.agents = (res.data && res.data.rows) || []
      }).catch(() => { this.agents = [] })
    },
    loadThemes() {
      listThemes().then(res => { this.themes = res.data || [] }).catch(() => { this.themes = [] })
    },
    themeLabels(ids) {
      const selected = String(ids || '').split(',').filter(Boolean)
      return selected.map(id => {
        const item = this.themes.find(theme => String(theme.aid) === String(id))
        return item ? item.topic_name : ('#' + id)
      }).join('、')
    },
    agentLabel(a) {
      const name = a.real_name || a.nickname || a.account || ('#' + a.id)
      return name + ' · ' + (a.mobile || '-') + ' · #' + a.id
    },
    onPidChange(value) { this.form.pid = Number(value) || 0 },
    openCreate() { this.form = emptyForm(); this.themeIdList = []; this.modalVisible = true },
    openEdit(record) {
      this.form = { ...emptyForm(), ...record, pid: Number(record.pid) || 0 }
      this.themeIdList = String(record.themeIds || '').split(',').filter(Boolean)
      this.modalVisible = true
    },
    closeModal() { this.modalVisible = false; this.form = emptyForm(); this.themeIdList = [] },
    submit() {
      this.$refs.categoryForm.validate(valid => {
        if (!valid) return
        this.saving = true
        const payload = {
          ...this.form,
          pid: Number(this.form.pid) || 0,
          themeIds: this.themeIdList.join(','),
          userId: this.scope === 'DK' && this.formLevel === 1 ? this.form.userId : 0
        }
        const request = this.form.aid ? updateCategory(this.scope, payload) : addCategory(this.scope, payload)
        request.then(() => { this.$message.success('保存成功'); this.closeModal(); this.load() }).finally(() => { this.saving = false })
      })
    },
    toggleStatus(record) {
      toggleCategoryStatus(this.scope, record.aid).then(() => { this.$message.success('状态已更新'); this.load() })
    },
    remove(record) {
      deleteCategory(this.scope, record.aid).then(() => { this.$message.success('删除成功'); this.load() })
    }
  }
}
</script>

<style scoped>
.catalog-heading { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px; }
.catalog-heading h2 { margin-bottom: 4px; }
.catalog-heading p, .muted { color: #8c8c8c; font-size: 12px; }
.upload-hint { color: #8c8c8c; font-size: 12px; line-height: 20px; }
.colour-field { display: flex; align-items: center; gap: 8px; }
.colour-picker { width: 42px; height: 32px; padding: 2px; border: 1px solid #d9d9d9; border-radius: 4px; background: #fff; cursor: pointer; }
.colour-value { display: inline-flex; align-items: center; gap: 6px; }
.colour-swatch { display: inline-block; width: 18px; height: 18px; border: 1px solid #d9d9d9; border-radius: 3px; }
.service-cell { display: flex; align-items: center; gap: 8px; text-align: left; }
.agent-option { display: flex; align-items: center; gap: 8px; }
.status-tag { cursor: pointer; }
</style>
